import { useQueryClient } from '@tanstack/react-query';
import { plainToClass } from 'class-transformer';
import React, { useEffect, useMemo } from 'react';
import useCookie from 'react-use-cookie';
import { io, Socket } from 'socket.io-client';

import { me, refresh } from '@/api/index';
import { AuthContext } from '@/providers/auth/AuthContext';
import { useAuthStore } from '@/store/useUser';
import { AuthStatusEnum, IUser, IUserLogin } from '@/types/auth';

import { baseURL } from '../../api/config';
const TOKEN_EXPIRE_OFFSET = 1000 * 60 * 5; // 5 minutes

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [login, setLogin] = React.useState<AuthStatusEnum>(AuthStatusEnum.LOADING);
  const [userCookie, setUserCookie] = useCookie('user', '');
  const [ws, setWs] = React.useState<Socket | null>(null);
  const queryClient = useQueryClient();

  const authStore = useAuthStore();

  const [counter, setCounter] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCounter((prev) => prev + 1);
    }, 3000000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const user = useMemo(() => (userCookie ? plainToClass(IUserLogin, JSON.parse(userCookie)) : null), [userCookie]);

  const logOut = React.useCallback(() => {
    setUserCookie('');
    setLogin(AuthStatusEnum.LOGGED_OUT);
    authStore.setUserData(null);
    (async () => {
      void queryClient.cancelQueries();
      void queryClient.removeQueries();
    })();
  }, [queryClient]);

  const tryRefreshToken = React.useCallback(() => {
    if (!user) {
      return;
    }
    try {
      const refreshTokenExpire = user.refreshToken.expires.getTime();

      const now = new Date().getTime();

      if (refreshTokenExpire - now < TOKEN_EXPIRE_OFFSET) {
        // REFRESH TOKEN EXPIRED
        console.log('REFRESH TOKEN EXPIRED');
        logOut();
      }

      // REFRESH HERE

      refresh(user.refreshToken.token).then((data) => {
        setUserCookie(JSON.stringify(data));
      });
    } catch (e) {
      logOut();
    }
  }, [user, logOut, setUserCookie]);

  useEffect(() => {
    if (!user || !authStore?.user) {
      logOut();
      return;
    }
    try {
      const tokenExpire = user.accessToken.expires.getTime();

      const now = new Date().getTime();

      if (tokenExpire - now < TOKEN_EXPIRE_OFFSET) {
        // TOKEN EXPIRED
        console.log('TOKEN EXPIRED');
        tryRefreshToken();
        return;
      }

      const tokenTime = tokenExpire - now;

      me()
        .then((resp) => {
          // if (resp?.abilities?.abilities) {
          const abilities = resp?.abilities?.abilities ? JSON.parse(resp?.abilities?.abilities) : [];
          const groups = resp?.abilities?.groups ?? [];

          console.log({
            sidebar: 'MERESP',
            resp,
            GroupsSet: groups
          });
          authStore.setAbilities({
            abilities
          });
          authStore.setGroups(groups);
          // } else {
          //   authStore.setGroups([]);
          // }

          setLogin(AuthStatusEnum.LOGGED_IN);
        })
        .catch((err) => {
          // Wait
          logOut();
          console.log(err);
        });
    } catch (e) {
      logOut();
    }
  }, [tryRefreshToken, user, counter, logOut]);

  React.useEffect(() => {
    if (!user) {
      return;
    }

    try {
      const token = user.accessToken.token;

      const url = new URL(baseURL);

      const socketio = io(url.origin, {
        auth: {
          token
        },
        transports: ['websocket'],
        path: (url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`) + 'socket.io'
      });

      setWs(socketio);

      return () => {
        socketio.disconnect();
        setWs(null);
      };
    } catch (e) {
      logOut();
    }
  }, [user]);

  const value = React.useMemo(
    () => ({ ws, login, setUserCookie, user: user?.user as IUser, logOut }),
    [ws, login, setUserCookie, user, logOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
